import { useRef, useLayoutEffect } from 'react';
import { Header, Content, InlineHeader, InlineSection, HeaderRenamePillButton, useScrollable } from '@/components/DashboardComponents'
import { Button } from '@/form/FormComponents'
import { useNavigate, useLoaderData, useRevalidator } from 'react-router'
import { useUser } from '@/systems/Authentication'
import { useShareModal } from '@/components/FilesystemShareModals';
import { useMove, useTrash, useRenameRubricModal, useCopyRubricModal, useSharedWithMe, useHandleRestore } from '@/pages/Filesystem';
import { ShareButton } from '@/components/ShareButton';
import { Icon } from '@/components/Icon';
import { useBreakpoint } from '@/hooks/useIsMobile';
import { RubricEditorTable, convertSectionsToRows } from '@/pages/RubricLayout';

export function RubricOverview() {
  const navigate = useNavigate();
  const revalidator = useRevalidator();
  const { file, rubric } = useLoaderData().data.value;
  const { uid } = useUser();

  const [handleShare] = useShareModal();
  const { moveMenuItem } = useMove({ file, folder: file.folder, view: "rubrics" });
  const { trashMenuItem } = useTrash({ file, folder: file.folder, view: "rubrics", dont_show_remove_option: true, go_back_upon_trash: true });
  const [openRenameRubricModal] = useRenameRubricModal();
  const [openCopyRubricModal] = useCopyRubricModal();

  const { addToMyMenuItem, openAddToMyModal } = useSharedWithMe({ file, is_shared: file._is_directly_shared });
  const show_add_button = useBreakpoint((width) => width > 700);

  function handleCopyRubric() {
    openCopyRubricModal({ file, show_choose_location: true, append_copy_to_title: ["edit", "owner"].includes(file._level), callback: ({ item_id }) => {
      navigate(`/rubrics/${item_id}`);
    } });
  }

  const handleRename = () => {
    openRenameRubricModal({
      file,
      callback: () => {
        revalidator.revalidate();
      }
    })
  }

  const menuItems = [
    ...addToMyMenuItem,
    {
      label: 'Rename',
      icon: 'rename-outline',
      onClick: handleRename,
      disabled: !["edit", "owner"].includes(file._level),
    },
    {
      label: 'Make a copy',
      icon: 'file-copy',
      onClick: handleCopyRubric,
    },
    "separator",
    {
      label: 'Share',
      icon: 'account-plus-outline',
      onClick: () => {handleShare(file)},
      disabled: !["edit", "owner"].includes(file._level),
    },
    ...moveMenuItem,
    ...(trashMenuItem.length > 0 ? ["separator"] : []),
    ...trashMenuItem
  ]

  const copy_banner = file._added_copy_item_id && file._is_directly_shared && (
    <div className="callout_hint_box">
      <div>
        <div><Icon name="file-copy"/></div>
        <div className="links">You made your own copy of this rubric. <a onClick={() => {navigate(`/rubrics/${file._added_copy_item_id}`)}}>Open my copy</a></div>
      </div>
    </div>
  )

  const { handleRestore, is_restoring } = useHandleRestore({ file });

  const trash_banner = file.trashed && (
    <div className="callout_hint_box">
      <div>
        <div><Icon name="trash-can-outline"/></div>
        <div>This rubric is in the trash and will be permanently deleted soon.</div>
        {(file.trashed_head && file.uid === uid) && <div className="callout_small_button">
          <Button text="Restore" icon="history" className="hollow small" onClick={handleRestore} loading={is_restoring}/>
        </div>}
      </div>
    </div>
  )

  const mobile_button_sizes = useBreakpoint((width) => width < 400);

  const { scroller_ref } = useScrollable();

  const rubricContainerRef = useRef(null);
  const scaleContentRef = useRef(null);
  const stickyHeaderRef = useRef(null);
  const minWidth = 1380;
  const minScale = 0.7;

  useLayoutEffect(() => {
    const rubricContainer = rubricContainerRef.current;
    const content = scaleContentRef.current;
    const stickyHeader = stickyHeaderRef.current;
    if (!rubricContainer || !content) return;

    const contentContainer = rubricContainer.closest('.dashboard_content_inner');

    const calculate = () => {
      // Get padding to calculate true available width
      const style = contentContainer ? getComputedStyle(contentContainer) : null;
      const paddingLeft = style ? parseFloat(style.paddingLeft) : 0;
      const paddingRight = style ? parseFloat(style.paddingRight) : 0;
      const clientWidth = contentContainer ? contentContainer.clientWidth : rubricContainer.clientWidth;
      const availableWidth = clientWidth - paddingLeft - paddingRight;
      
      const scale = Math.max(minScale, Math.min(1, availableWidth / minWidth));
      content.style.zoom = scale;
      
      if (stickyHeader) {
        stickyHeader.style.width = `${availableWidth}px`;
      }
    };

    calculate();

    const observer = new ResizeObserver(calculate);
    observer.observe(contentContainer || rubricContainer);
    
    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <>
      <Header title={<HeaderRenamePillButton text={file.title} onClick={handleRename} disabled={!["edit", "owner"].includes(file._level)} />} backButton={true} menuItems={menuItems}>
        {show_add_button && file._is_directly_shared && !(file._added_shortcut_id || file._added_copy_item_id) && <Button icon={"bullseye-arrow-plus"} text={`Add to my rubrics`} onClick={openAddToMyModal} />}
        {!mobile_button_sizes && <ShareButton file={file} />}     
      </Header>
      <Content>
        <div ref={rubricContainerRef} style={{ width: 'max-content' }}>
          <div ref={stickyHeaderRef} style={{ position: 'sticky', left: 0 }}>
            {copy_banner}
            {trash_banner}
            <InlineHeader>
              {["edit", "owner"].includes(file._level) ? (
                <Button icon="pencil-outline" className="hollow" backgroundColor="var(--header)" text="Edit rubric" onClick={() => {navigate(`/rubrics/${file.item_id}/edit`)}} disabled={!["edit", "owner"].includes(file._level) || file.trashed}/>
              ) : (
                <Button icon="copy-and-edit" className="hollow" backgroundColor="var(--header)" text="Make a copy to edit" onClick={handleCopyRubric}/>
              )}
            </InlineHeader>
          </div>
          <InlineSection>
            <div 
              ref={scaleContentRef}
              style={{
                width: minWidth,
                minWidth: minWidth,
              }}
            >
              <RubricEditorTable rows={convertSectionsToRows(rubric.data.sections)} levels={rubric.data.levels} scoring={rubric.data.scoring} scroller_ref={scroller_ref} readOnly={true} />
            </div>
          </InlineSection>
        </div>
      </Content>
    </>
  )
}